import { reflect } from '@effector/reflect';
import { Button, Checkbox, Stack, Text, TextInput } from '@mantine/core';
import { combine } from 'effector';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { createBlockAccountModal } from 'features/block-account';
import { createDeleteAccountModal } from 'features/delete-account';
import { createUnblockAccountModal } from 'features/unblock-account';
import { ACCOUNT_NAME_MIN_LENGTH, ACCOUNT_NOTE_MAX_LENGTH } from 'entities/account';
import { $t, translated } from 'shared/lib';
import { accountPageModel, accountPageSettingsModel } from '../../model';
import cls from './account-page-settings.module.scss';

const BlockAccountModal = createBlockAccountModal(accountPageSettingsModel.blockAccountModel);

const UnblockAccountModal = createUnblockAccountModal(accountPageSettingsModel.unblockAccountModel);

const DeleteAccountModal = createDeleteAccountModal(accountPageSettingsModel.deleteAccountModel);

const NameField = reflect({
	view: TextInput,
	bind: {
		label: translated('edit-account.name'),
		placeholder: translated('edit-account.name'),
		errorProps: { 'data-test-id': 'edit-account-name-input-error' },
		error: combine(
			[accountPageSettingsModel.form.fields.name.$errors, $t],
			([errors, t]) =>
				errors.length > 0 &&
				t(`errors:validation.${errors[0]}`, {
					fieldName: t('edit-account.name'),
					minLength: ACCOUNT_NAME_MIN_LENGTH,
				}),
		),
		value: accountPageSettingsModel.form.fields.name.$value,
		onChange: (e) => accountPageSettingsModel.form.fields.name.change(e.target.value),
		required: true,
	},
});

const NoteField = reflect({
	view: TextInput,
	bind: {
		label: translated('edit-account.note'),
		placeholder: translated('edit-account.note'),
		errorProps: { 'data-test-id': 'edit-account-note-input-error' },
		error: combine(
			[accountPageSettingsModel.form.fields.note.$errors, $t],
			([errors, t]) =>
				errors.length > 0 &&
				t(`errors:validation.${errors[0]}`, {
					fieldName: t('edit-account.note'),
					maxLength: ACCOUNT_NOTE_MAX_LENGTH,
				}),
		),
		value: accountPageSettingsModel.form.fields.note.$value,
		onChange: (e) => accountPageSettingsModel.form.fields.note.change(e.target.value),
	},
});

const CreateSubAccountsField = reflect({
	view: Checkbox,
	bind: {
		label: translated('edit-account.write-subaccounts'),
		disabled: accountPageSettingsModel.form.fields.canCreateSubAccounts.$disabled,
		checked: accountPageSettingsModel.form.fields.canCreateSubAccounts.$value,
		onChange: (e) =>
			accountPageSettingsModel.form.fields.canCreateSubAccounts.change(e.target.checked),
	},
});

const WriteCashdesksFIeld = reflect({
	view: Checkbox,
	bind: {
		label: translated('edit-account.write-cashdesks'),
		disabled: accountPageSettingsModel.form.fields.canWriteCashdesks.$disabled,
		checked: accountPageSettingsModel.form.fields.canWriteCashdesks.$value,
		onChange: (e) =>
			accountPageSettingsModel.form.fields.canWriteCashdesks.change(e.target.checked),
	},
});

const WriteUsersField = reflect({
	view: Checkbox,
	bind: {
		label: translated('edit-account.write-users'),
		disabled: accountPageSettingsModel.form.fields.canWriteUsers.$disabled,
		checked: accountPageSettingsModel.form.fields.canWriteUsers.$value,
		onChange: (e) => accountPageSettingsModel.form.fields.canWriteUsers.change(e.target.checked),
	},
});

const EditLimitsField = reflect({
	view: Checkbox,
	bind: {
		label: translated('edit-account.edit-limits'),
		disabled: accountPageSettingsModel.form.fields.canEditLimits.$disabled,
		checked: accountPageSettingsModel.form.fields.canEditLimits.$value,
		onChange: (e) => accountPageSettingsModel.form.fields.canEditLimits.change(e.target.checked),
	},
});

const BlockGamesField = reflect({
	view: Checkbox,
	bind: {
		label: translated('edit-account.block-games'),
		disabled: accountPageSettingsModel.form.fields.canBlockGames.$disabled,
		checked: accountPageSettingsModel.form.fields.canBlockGames.$value,
		onChange: (e) => accountPageSettingsModel.form.fields.canBlockGames.change(e.target.checked),
	},
});

const EditGameSettingsField = reflect({
	view: Checkbox,
	bind: {
		label: translated('edit-account.edit-game-settings'),
		disabled: accountPageSettingsModel.form.fields.canEditGameSettings.$disabled,
		checked: accountPageSettingsModel.form.fields.canEditGameSettings.$value,
		onChange: (e) =>
			accountPageSettingsModel.form.fields.canEditGameSettings.change(e.target.checked),
	},
});

const SaveButton = reflect({
	view: Button,
	bind: {
		type: 'submit',
		children: translated('account-page.save'),
		loading: accountPageSettingsModel.$pending,
		disabled: accountPageSettingsModel.form.$pristine,
		className: cls.accountPageSettingsFooterOption,
	},
});

export const AccountPageSettings = () => {
	const [account, submit] = useUnit([
		accountPageModel.$account,
		accountPageSettingsModel.form.submit,
	]);

	const { t } = useTranslation();

	if (account === null) {
		return null;
	}

	return (
		<>
			<form
				className={cls.accountPageSettings}
				onSubmit={(event) => {
					event.preventDefault();
					submit();
				}}
			>
				<NameField />
				<NoteField />
				<Stack gap={12}>
					<Text
						size='sm'
						fw={500}
					>
						{t('edit-account.permissions')}
					</Text>
					<CreateSubAccountsField />
					<WriteCashdesksFIeld />
					<WriteUsersField />
					<EditLimitsField />
					<EditGameSettingsField />
					<BlockGamesField />
				</Stack>
				<div className={cls.accountPageSettingsFooter}>
					<SaveButton />
					<div className={cls.accountPageSettingsFooterOptions}>
						{account.isLocked ? (
							<UnblockAccountModal.Button
								modalData={account}
								className={cls.accountPageSettingsFooterOption}
							/>
						) : (
							<BlockAccountModal.Button
								modalData={account}
								className={cls.accountPageSettingsFooterOption}
							/>
						)}
						<DeleteAccountModal.Button
							modalData={account}
							className={cls.accountPageSettingsFooterOption}
						/>
					</div>
				</div>
			</form>
			<BlockAccountModal />
			<UnblockAccountModal />
			<DeleteAccountModal />
		</>
	);
};
